import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

/**
 * Legal — single component for static legal pages (Terms, Privacy).
 * Route: /legal/terms · /legal/privacy
 * App-Store/Play-Store launch requirement.
 */
const TERMS = `
# Terms of Service

_Effective date: ${new Date().toLocaleDateString('en-US', { dateStyle: 'long' })}_

Welcome to ASAP, a social platform for discovering and joining real-life plans in the UAE. By using ASAP, you agree to these Terms.

## 1. Eligibility
You must be 13+ to use ASAP. If you're under 18, you confirm a parent or guardian has approved your use.

## 2. Your account
You're responsible for keeping your password safe and for everything done from your account. Don't share access. Don't impersonate anyone.

## 3. Community guidelines
You may not post, host, or share content that is:
- Harassing, threatening, hateful, or discriminatory
- Sexual, violent, or otherwise inappropriate
- Spam, scam, or misleading
- A violation of UAE law

Repeated or serious violations may result in warnings, suspension, or permanent removal of your account at ASAP's discretion. Our Trust & Safety team reviews reports manually.

## 4. Real-life plans
ASAP helps people meet in person. **Meet responsibly**. Choose public places. Trust your gut. Report unsafe activity through the in-app Report button. ASAP is not a party to plans you host or join and is not responsible for what happens at them.

## 5. Content you post
You keep ownership of what you post. By posting, you grant ASAP a worldwide, royalty-free license to display and distribute it inside the app. Don't post content you don't have the rights to.

## 6. Things we don't do
- We don't sell your personal data.
- We don't moderate every piece of content in advance — we respond to reports.
- We don't guarantee uptime.

## 7. Termination
You can delete your account anytime in Settings → Account. We may suspend or terminate accounts that violate these Terms.

## 8. Changes
We may update these Terms. Material changes will be flagged in the app.

## 9. Contact
For questions, write to **safety@asap.app**.
`;

const PRIVACY = `
# Privacy Policy

_Effective date: ${new Date().toLocaleDateString('en-US', { dateStyle: 'long' })}_

## What we collect
- **Account info**: email, username, display name, optional bio, photo, interests, city.
- **Plans you create or join**: title, description, location, attendees, status.
- **Content you post**: stories, vibes, comments, chats.
- **Device data**: anonymised crash logs and basic analytics.

## What we don't collect
- Sensitive identifiers beyond what's needed for the service.
- Continuous background location.
- Your contacts unless you explicitly invite someone.

## How we use it
- To run ASAP — show your plans to friends, deliver chats and notifications.
- To keep the community safe — review reports, surface trusted hosts.
- To improve the product — anonymised metrics in the admin dashboard.

## Who we share with
- **Service providers** that make ASAP work (hosting, image CDN, transactional email).
- **Authorities** only when legally required.
- **No advertisers, no data brokers.**

## Your rights
- **Access & export** your data — write to **safety@asap.app**.
- **Delete your account** anytime in Settings → Account. Hosted plans are anonymised. User-generated content (stories, vibes, follows, joins) is hard-deleted.
- **Correct** anything wrong by editing your profile.

## Children
ASAP is not for users under 13.

## Retention
We retain your data while your account is active. Moderation actions and audit logs are retained for accountability even after account deletion.

## Contact
**safety@asap.app**
`;

export default function Legal() {
  const navigate = useNavigate();
  const { kind } = useParams();
  const location = useLocation();
  // Resolve which legal page: /terms · /privacy · /legal/terms · /legal/privacy
  const path = location.pathname.toLowerCase();
  const resolvedKind = kind || (path.includes('privacy') ? 'privacy' : 'terms');
  const isTerms = resolvedKind === 'terms';
  const title = isTerms ? 'Terms of Service' : 'Privacy Policy';
  const body = isTerms ? TERMS : PRIVACY;

  return (
    <div className="min-h-[100dvh] bg-[#F7F6F2] pb-32" data-testid={`legal-page-${resolvedKind}`}>
      <div className="sticky top-0 z-30 bg-[#F7F6F2]/95 backdrop-blur-xl border-b border-black/[0.05]" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="flex items-center gap-2 px-3 pt-3 pb-3">
          <button onClick={() => { if (window.history.length > 1) navigate(-1); else navigate('/'); }} data-testid="legal-back" className="w-10 h-10 rounded-full bg-white border border-black/[0.06] flex items-center justify-center active:scale-90">
            <ArrowLeft size={18} className="text-[#111111]" />
          </button>
          <h1 className="text-[16px] font-extrabold text-[#111111] tracking-tight">{title}</h1>
        </div>
      </div>

      <article className="px-5 pt-6 prose-asap">
        {body.split('\n').map((line, i) => {
          if (line.startsWith('# '))   return <h1 key={i} className="text-[22px] font-extrabold text-[#111111] tracking-tight mt-2 mb-1">{line.slice(2)}</h1>;
          if (line.startsWith('## '))  return <h2 key={i} className="text-[15px] font-bold text-[#111111] mt-5 mb-2">{line.slice(3)}</h2>;
          if (line.startsWith('- '))   return <li key={i} className="text-[13.5px] text-[#4B4F55] ml-5 list-disc leading-relaxed">{renderInline(line.slice(2))}</li>;
          if (line.startsWith('_'))    return <p key={i} className="text-[11px] uppercase tracking-wider text-[#9B9EA3] mt-1 mb-3">{line.replace(/^_|_$/g, '')}</p>;
          if (!line.trim())            return <div key={i} className="h-2" />;
          return <p key={i} className="text-[13.5px] text-[#4B4F55] leading-relaxed mb-2">{renderInline(line)}</p>;
        })}
      </article>
    </div>
  );
}

// minimal **bold** and inline rendering — no extra deps
function renderInline(s) {
  const parts = s.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith('**') && p.endsWith('**')
      ? <strong key={i} className="font-bold text-[#111111]">{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>
  );
}
